import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import api from '../services/api';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const { socket, isConnected, currentSession, registerDevice, setCurrentSession, finalGameWinner } = useGame();

  const [timeRemaining, setTimeRemaining] = useState(null);
  const [helpRequests, setHelpRequests] = useState([]);
  const [pendingQuestion4, setPendingQuestion4] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/login');
      return;
    }

    // Verify token
    api.post('/auth/verify', { token }).catch(() => {
      localStorage.removeItem('adminToken');
      navigate('/login');
    });
  }, [navigate]);

  // Set up socket listeners - this should run whenever socket is available
  useEffect(() => {
    if (!socket) return;

    // Listen for updates - set up listeners immediately
    const handleTeamRegistered = () => {
      fetchCurrentSession();
    };

    const handleScoreUpdated = () => {
      fetchCurrentSession();
    };

    const handleHelpRequested = (data) => {
      setHelpRequests(prev => [...prev, data]);
    };

    const handleHelpAcknowledged = (data) => {
      setHelpRequests(prev => prev.filter(req => req.teamId !== data.teamId));
    };

    const handleFinalGameScoreUpdated = () => {
      fetchCurrentSession();
    };

    const handleQuestion4Pending = (data) => {
      console.log('✅ Dashboard received dining room question 4 pending:', data);
      setPendingQuestion4(data);
    };

    // Set up all listeners immediately
    socket.on('team:registered', handleTeamRegistered);
    socket.on('score:updated', handleScoreUpdated);
    socket.on('help:requested', handleHelpRequested);
    socket.on('help:acknowledged', handleHelpAcknowledged);
    socket.on('final-game:score-updated', handleFinalGameScoreUpdated);
    socket.on('dining-room:question4:pending', handleQuestion4Pending);

    console.log('✅ Dashboard listeners set up for socket:', socket.id);

    return () => {
      if (socket) {
        // Remove all listeners with specific handlers
        socket.off('team:registered', handleTeamRegistered);
        socket.off('score:updated', handleScoreUpdated);
        socket.off('help:requested', handleHelpRequested);
        socket.off('help:acknowledged', handleHelpAcknowledged);
        socket.off('final-game:score-updated', handleFinalGameScoreUpdated);
        socket.off('dining-room:question4:pending', handleQuestion4Pending);
      }
    };
  }, [socket]);

  // Register device when connected
  useEffect(() => {
    if (isConnected && socket) {
      console.log('📱 Registering dashboard device...');
      registerDevice('dashboard');

      // Listen for registration confirmation
      const handleDashboardRegistered = () => {
        console.log('✅ Dashboard successfully registered and joined room');
      };

      socket.on('dashboard:registered', handleDashboardRegistered);

      return () => {
        if (socket) {
          socket.off('dashboard:registered', handleDashboardRegistered);
        }
      };
    }
  }, [isConnected, socket]);

  // Timer countdown
  useEffect(() => {
    if (currentSession?.status === 'active' && currentSession.startTime) {
      const interval = setInterval(() => {
        const start = new Date(currentSession.startTime);
        const duration = currentSession.duration * 60 * 1000; // Convert to milliseconds
        const elapsed = Date.now() - start.getTime();
        const remaining = Math.max(0, duration - elapsed);

        setTimeRemaining(remaining);

        // Auto-end game when time runs out
        if (remaining === 0 && currentSession.status === 'active') {
          handleEndGame();
        }
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setTimeRemaining(null);
    }
  }, [currentSession]);

  const fetchCurrentSession = async () => {
    try {
      const response = await api.get('/game/current');
      if (response.data.success) {
        setCurrentSession(response.data.session);
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  const handleStartGame = async () => {
    try {
      const response = await api.post('/game/start');
      if (response.data.success) {
        setCurrentSession(response.data.session);
      }
    } catch (error) {
      console.error('Error starting game:', error);
      alert('Failed to start game');
    }
  };

  const handleStartNextRound = async () => {
    if (!currentSession) return;

    try {
      const response = await api.post(`/game/${currentSession.sessionId}/next-round`);
      if (response.data.success) {
        setCurrentSession(response.data.session);
      }
    } catch (error) {
      console.error('Error starting next round:', error);
      alert('Failed to start next round');
    }
  };

  const handleActivateGame = async () => {
    if (!currentSession) return;

    try {
      await api.post(`/game/${currentSession.sessionId}/activate`);
    } catch (error) {
      console.error('Error activating game:', error);
      alert('Failed to activate game');
    }
  };

  const handlePauseGame = async () => {
    if (!currentSession) return;

    try {
      await api.post(`/game/${currentSession.sessionId}/pause`);
    } catch (error) {
      console.error('Error pausing game:', error);
    }
  };

  const handleResumeGame = async () => {
    if (!currentSession) return;

    try {
      await api.post(`/game/${currentSession.sessionId}/resume`);
    } catch (error) {
      console.error('Error resuming game:', error);
    }
  };

  const handleEndGame = async () => {
    if (!currentSession) return;

    try {
      await api.post(`/game/${currentSession.sessionId}/end`);
    } catch (error) {
      console.error('Error ending game:', error);
    }
  };

  const handleToggleTabletsLock = async () => {
    if (!currentSession) return;

    // Optimistic update - immediately update UI
    const newLockState = !currentSession.tabletsLocked;
    setCurrentSession({ ...currentSession, tabletsLocked: newLockState });

    try {
      if (newLockState) {
        await api.post(`/game/${currentSession.sessionId}/lock-tablets`);
      } else {
        await api.post(`/game/${currentSession.sessionId}/unlock-tablets`);
      }
    } catch (error) {
      console.error('Error toggling tablets lock:', error);
      // Revert on error
      setCurrentSession({ ...currentSession, tabletsLocked: !newLockState });
      alert('Failed to toggle tablets lock state');
    }
  };

  const handleStartFinalGame = async () => {
    if (!currentSession) return;

    const confirmed = window.confirm('Start the Final Game? This will unlock all tablets for the final 10 questions.');
    if (!confirmed) return;

    try {
      await api.post(`/game/${currentSession.sessionId}/final-game`);
    } catch (error) {
      console.error('Error starting final game:', error);
      alert('Failed to start final game');
    }
  };

  const handleTerminateGame = async () => {
    if (!currentSession) return;

    const confirmed = window.confirm(
      'Terminate the game? This will:\n' +
      '- Reset all scores to zero\n' +
      '- Clear final game scores\n' +
      '- Reset game status\n' +
      '- Return to start screen\n\n' +
      'This action cannot be undone. Continue?'
    );
    if (!confirmed) return;

    try {
      await api.post(`/game/${currentSession.sessionId}/terminate`);
      fetchCurrentSession();
    } catch (error) {
      console.error('Error terminating game:', error);
      alert('Failed to terminate game');
    }
  };

  const handleAcknowledgeHelp = (teamId) => {
    if (socket) {
      socket.emit('help:acknowledge', {
        sessionId: currentSession?.sessionId,
        teamId
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/login');
  };

  const handleQuestion4Judgment = async (approved) => {
    if (!pendingQuestion4 || !socket) return;

    socket.emit('dining-room:question4:judge', {
      sessionId: pendingQuestion4.sessionId,
      teamId: pendingQuestion4.teamId,
      questionId: pendingQuestion4.questionId,
      approved
    });

    setPendingQuestion4(null);
  };

  const getTabletUrl = (section) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/tablet/${section}`;
  };

  const handleCopyLink = async (section) => {
    const url = getTabletUrl(section);
    try {
      await navigator.clipboard.writeText(url);
      // Show temporary feedback
      const button = document.querySelector(`[data-section="${section}"]`);
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✓ Copied!';
        button.style.color = '#4caf50';
        setTimeout(() => {
          button.textContent = originalText;
          button.style.color = '';
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy link');
    }
  };

  const handleTabletLinkClick = (section) => {
    const url = getTabletUrl(section);
    window.open(url, '_blank');
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = () => {
    if (!currentSession) return <span className="status-badge waiting">No Active Session</span>;

    const roundText = currentSession.currentRound > 0 ? ` - Round ${currentSession.currentRound}` : '';

    switch (currentSession.status) {
      case 'waiting':
        return <span className="status-badge waiting">Waiting to Start{roundText}</span>;
      case 'team-selection':
        return <span className="status-badge team-selection">Team Selection</span>;
      case 'active':
        return <span className="status-badge active">Active{roundText}</span>;
      case 'paused':
        return <span className="status-badge paused">Paused{roundText}</span>;
      case 'final-game':
        return <span className="status-badge final-game">Final Game</span>;
      case 'final-game-ended':
        return <span className="status-badge final-game-ended">Final Game Ended</span>;
      case 'ended':
        // For round end - show proper round name
        if (currentSession.currentRound > 0 && currentSession.currentRound <= 4) {
          const roundNames = ['First', 'Second', 'Third', 'Fourth'];
          return <span className="status-badge ended">{roundNames[currentSession.currentRound - 1]} round ended</span>;
        }
        return <span className="status-badge ended">Round Ended</span>;
      default:
        return <span className="status-badge">Unknown</span>;
    }
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <div className={`dashboard-sidebar ${sidebarExpanded ? 'expanded' : 'collapsed'}`}>
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarExpanded(!sidebarExpanded)}
          aria-label="Toggle sidebar"
        >
          {sidebarExpanded ? '◀' : '▶'}
        </button>

        <div className="sidebar-content">
          <h3 className="sidebar-title">Tablet Links</h3>

          {['wine-cellar', 'study', 'fuse-box', 'dining-room'].map((section) => (
            <div key={section} className="sidebar-item">
              <a
                href={getTabletUrl(section)}
                onClick={(e) => {
                  e.preventDefault();
                  handleTabletLinkClick(section);
                }}
                className="sidebar-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="sidebar-link-icon">
                  {section === 'wine-cellar' && '🍷'}
                  {section === 'study' && '📚'}
                  {section === 'dining-room' && '🍽️'}
                  {section === 'fuse-box' && '⚡'}
                </span>
                {sidebarExpanded && (
                  <span className="sidebar-link-text">
                    {section.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                )}
              </a>
              {sidebarExpanded && (
                <button
                  className="sidebar-copy-btn"
                  onClick={() => handleCopyLink(section)}
                  data-section={section}
                  title="Copy link"
                >
                  📋
                </button>
              )}
            </div>
          ))}

          <div className="sidebar-divider"></div>

          <div className="sidebar-item">
            <a
              href="/history"
              onClick={(e) => {
                e.preventDefault();
                navigate('/history');
              }}
              className="sidebar-link history-link"
            >
              <span className="sidebar-link-icon">📊</span>
              {sidebarExpanded && (
                <span className="sidebar-link-text">History</span>
              )}
            </a>
          </div>
        </div>
      </div>

      <div className="dashboard-main-content">
        <header className="dashboard-header">
          <div className="header-left">
            <h1>🎮 Gamemaster Dashboard</h1>
            <div className="connection-status">
              {isConnected ? (
                <span className="connected">● Connected</span>
              ) : (
                <span className="disconnected">● Disconnected</span>
              )}
            </div>
          </div>
          <button onClick={handleLogout} className="btn-gold logout-btn">
            Logout
          </button>
        </header>

        <div className="dashboard-content">
          {/* Game Status */}
          <div className="card status-card">
            <h2>Game Status</h2>
            <div className="status-info">
              {getStatusBadge()}
              {timeRemaining !== null && (
                <div className="timer-display">
                  <span className="timer-label">Time Remaining:</span>
                  <span className={`timer-value ${timeRemaining < 60000 ? 'warning' : ''}`}>
                    {formatTime(timeRemaining)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Game Controls */}
          <div className="card controls-card">
            <h2>Game Controls</h2>
            <div className="controls-grid">
              {/* Show Start New Game only when no session or when final game has ended */}
              {!currentSession || currentSession.status === 'final-game-ended' ? (
                <button onClick={handleStartGame} className="btn-gold">
                  Start New Game
                </button>
              ) : null}

              {currentSession?.status === 'waiting' && currentSession.currentRound > 0 && (
                <button onClick={handleActivateGame} className="btn-gold">
                  Activate Game (Start Timer)
                </button>
              )}

              {currentSession?.status === 'team-selection' && (
                <button onClick={handleActivateGame} className="btn-gold">
                  Activate Game (Start Timer)
                </button>
              )}

              {currentSession?.status === 'active' && (
                <>
                  <button onClick={handlePauseGame} className="btn-gold">
                    Pause Game
                  </button>
                  <button onClick={handleEndGame} className="btn-gold">
                    End Game
                  </button>
                </>
              )}

              {currentSession?.status === 'paused' && (
                <button onClick={handleResumeGame} className="btn-gold">
                  Resume Game
                </button>
              )}

              {currentSession && currentSession.status !== 'ended' && (
                <button onClick={handleToggleTabletsLock} className="btn-gold">
                  {currentSession.tabletsLocked ? 'Unlock Tablets' : 'Lock Tablets'}
                </button>
              )}

              {/* Start Next Round button - shown after rounds 1, 2, 3 (not after round 4) */}
              {currentSession && currentSession.status === 'ended' && currentSession.currentRound > 0 && currentSession.currentRound < 4 && !currentSession.finalGameActive && (
                <button onClick={handleStartNextRound} className="btn-gold">
                  Start Next Round
                </button>
              )}

              {/* Start Final Game - shown after round 4 or if all rounds completed */}
              {currentSession && currentSession.status === 'ended' && currentSession.currentRound === 4 && currentSession.finalGameActive === false && currentSession.teamProgress && currentSession.teamProgress.length > 0 && (
                <button onClick={handleStartFinalGame} className="btn-gold">
                  Start Final Game
                </button>
              )}

              {currentSession && currentSession.status === 'final-game' && currentSession.finalGameActive && (
                <button onClick={handleTerminateGame} className="btn-danger">
                  Terminate Game
                </button>
              )}

              {/* Hide other controls when final game ended */}
              {currentSession?.status === 'final-game-ended' && (
                <p className="info-text">Final game has ended. Press "Start New Game" to begin a new session.</p>
              )}
            </div>
          </div>

          {/* Team Progress */}
          {currentSession?.teamProgress && currentSession.teamProgress.length > 0 && (
            <div className="card teams-card">
              <h2>Team Progress</h2>
              <div className="teams-grid">
                {currentSession.teamProgress.map((team, index) => (
                  <div key={index} className="team-card">
                    <h3>{team.teamName}</h3>
                    <div className="team-score">
                      Total Score: <strong>{team.totalScore}</strong>
                    </div>
                    <div className="section-scores">
                      {Object.entries(team.sections).map(([section, data]) => (
                        <div key={section} className="section-score">
                          <span className="section-name">
                            {section.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                          </span>
                          <span className="score-value">{data.score}</span>
                          {data.completed && <span className="completed-badge">✓</span>}
                        </div>
                      ))}
                    </div>
                    {team.helpRequested && (
                      <div className="help-alert">
                        🆘 Help Requested!
                        <button
                          onClick={() => handleAcknowledgeHelp(team.teamId)}
                          className="btn-gold small-btn"
                        >
                          Acknowledge
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Game Winner Display */}
          {currentSession?.status === 'final-game-ended' && finalGameWinner && (
            <div className="card winner-card">
              <h2>🏆 Game Winner</h2>
              <div className="winner-display">
                <div className="winner-trophy">🏆</div>
                <h3 className="winner-name">{finalGameWinner.teamName}</h3>
                <p className="winner-score">Final Game Score: {finalGameWinner.score} points</p>
              </div>
            </div>
          )}

          {/* Final Game Scorecard - show when final game is active OR when final game ended */}
          {(currentSession?.finalGameActive || currentSession?.status === 'final-game-ended') &&
            currentSession?.finalGameScores && currentSession.finalGameScores.length > 0 && (
              <div className="card final-game-scorecard">
                <h2>🏆 Final Game Scores</h2>
                <div className="final-game-teams-grid">
                  {currentSession.finalGameScores.map((teamScore, index) => {
                    const teamProgress = currentSession.teamProgress?.find(
                      tp => tp.teamId.toString() === teamScore.teamId.toString()
                    );
                    return (
                      <div key={index} className="final-game-team-card">
                        <h3>{teamScore.teamName}</h3>
                        <div className="final-game-score-display">
                          <div className="final-game-score-item">
                            <span className="score-label">Final Game Score:</span>
                            <span className={`score-value ${teamScore.score >= 0 ? 'positive' : 'negative'}`}>
                              {teamScore.score > 0 ? '+' : ''}{teamScore.score}
                            </span>
                          </div>
                          <div className="final-game-score-item">
                            <span className="score-label">Questions Answered:</span>
                            <span className="score-value">{teamScore.answeredQuestions.length} / 10</span>
                          </div>
                          {currentSession?.status !== 'final-game-ended' && (
                            <div className="final-game-score-item">
                              <span className="score-label">Current Question:</span>
                              <span className="score-value">{teamScore.currentQuestionIndex + 1} / 10</span>
                            </div>
                          )}
                          {teamProgress && (
                            <div className="final-game-score-item total">
                              <span className="score-label">Total Score (All Rounds):</span>
                              <span className="score-value total-score">{teamProgress.totalScore}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {/* Help Requests */}
          {helpRequests.length > 0 && (
            <div className="card help-card">
              <h2>🆘 Active Help Requests</h2>
              <div className="help-requests">
                {helpRequests.map((request, index) => (
                  <div key={index} className="help-request">
                    <div className="help-info">
                      <strong>{request.teamName}</strong> needs help at{' '}
                      <strong>{request.section.replace(/-/g, ' ')}</strong>
                      <br />
                      <small>{new Date(request.time).toLocaleTimeString()}</small>
                    </div>
                    <button
                      onClick={() => handleAcknowledgeHelp(request.teamId)}
                      className="btn-gold small-btn"
                    >
                      Acknowledge
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Dining Room Question 4 Approval Modal */}
        {pendingQuestion4 && (
          <div className="modal-overlay" onClick={() => { }}>
            <div className="modal-content question4-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Dining Room - Question 4 Answer</h2>
              <div className="question4-info">
                <p><strong>Team:</strong> {pendingQuestion4.teamName}</p>
                <p><strong>Answer:</strong></p>
                <div className="question4-answer-box">
                  {pendingQuestion4.answer}
                </div>
              </div>
              <div className="question4-actions">
                <button
                  onClick={() => handleQuestion4Judgment(true)}
                  className="btn-gold approve-btn"
                >
                  Yes (+100 points)
                </button>
                <button
                  onClick={() => handleQuestion4Judgment(false)}
                  className="btn-danger reject-btn"
                >
                  No (-20 points)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;

